import { useEffect, type ReactNode } from 'react';

/**
 * Выезжающая справа панель нового визуала (ПЕРЕДАЧА-В-БОЙ.md §3): просмотр дела или заказа, широкая форма
 * «Новый заказ». Затемняет фон (клик по фону и Esc закрывают). Разметка и стили — .cx-scrim / .cx-drawer из today.css.
 * `tone` красит верх панели: o — горит, n — от клиента, p/k — обычная. `wide` — 560 px под форму.
 */
export function Drawer({ open, onClose, title, tone = 'p', wide = false, children }: { open: boolean; onClose: () => void; title: string; tone?: 'o' | 'n' | 'g' | 'k' | 'p'; wide?: boolean; children: ReactNode }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  return (
    <>
      <div className={`cx-scrim${open ? ' on' : ''}`} onMouseDown={onClose} aria-hidden="true" />
      <aside className={`cx-drawer cx-drawer--${tone}${wide ? ' cx-drawer--wide' : ''}${open ? ' on' : ''}`} role="dialog" aria-modal="true" aria-label={title} aria-hidden={!open}>
        {open ? children : null}
      </aside>
    </>
  );
}

/** Круглая стрелка-«вперёд» нового визуала. */
export const Arrow = ({ size = 14 }: { size?: number }) => (
  <svg className="icon" viewBox="0 0 16 16" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4" /></svg>
);
export const IconX = ({ size = 14 }: { size?: number }) => (
  <svg className="icon" viewBox="0 0 16 16" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" /></svg>
);
export const IconPlus = ({ size = 14 }: { size?: number }) => (
  <svg className="icon" viewBox="0 0 16 16" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true"><path d="M8 3v10M3 8h10" /></svg>
);

/* Иконки нового визуала для заявок (эталон erp-design/shell.js): почта, входящие, карандаш, скрепка, шеврон, назад, файл, предупреждение, чат. */
const svg = (d: string, size: number, sw = 1.5) => (
  <svg className="icon" viewBox="0 0 16 16" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>
);
export const IconMail = ({ size = 14 }: { size?: number }) => svg('M2 3.5h12v9H2zM2.3 4.3L8 8.8l5.7-4.5', size);
export const IconInbox = ({ size = 14 }: { size?: number }) => svg('M2 9.5l1.8-6h8.4l1.8 6v3.5H2zM2 9.5h3.5l.8 1.6h3.4l.8-1.6H14', size);
export const IconEdit = ({ size = 14 }: { size?: number }) => svg('M2.5 13.5l.6-2.8 7.6-7.6 2.2 2.2-7.6 7.6zM9.4 4.4l2.2 2.2', size);
export const IconClip = ({ size = 14 }: { size?: number }) => svg('M13 7.5l-5 5a3 3 0 01-4.3-4.2l5.6-5.6a2 2 0 012.8 2.8L6.7 11', size);
export const IconChev = ({ size = 14 }: { size?: number }) => svg('M4.5 6.5L8 10l3.5-3.5', size);
export const IconBack = ({ size = 14 }: { size?: number }) => svg('M13 8H3M7 4L3 8l4 4', size);
export const IconFile = ({ size = 14 }: { size?: number }) => svg('M4 1.8h5.5L12.5 5v9.2H4zM9.5 1.8V5h3', size);
export const IconWarn = ({ size = 14 }: { size?: number }) => svg('M8 2.2l6.2 11H1.8zM8 6.5v3.2M8 11.6h.01', size);
/** Поле в стиле новых окон (позиция заказа, склад, изделия): подпись капителью, контрол, подсказка или ошибка. */
export function Fld({ label, hint, error, children, missing }: { label: string; hint?: ReactNode; error?: string | undefined; children: ReactNode; missing?: boolean }) {
  return <label className={`mf${missing ? ' pm-missing' : ''}`}><span>{label}</span>{children}{error ? <small className="bad-t">{error}</small> : hint ? <small>{hint}</small> : null}</label>;
}

export const IconChat = ({ size = 14 }: { size?: number }) => svg('M2 3.5h12v8H8.5L5 14v-2.5H2z', size);
